import type { CommentInlineResolveProperties } from '@confluence/inline-comments-queries/entry-points/ActiveInlineCommentsQuery.ts';
import type { FooterResolvedProperties } from '@confluence/comments-panel-queries/entry-points/ActiveCommentsQuery.ts';

import { commentsi18n } from './commentsi18n.ts';
import { CommentStatus } from './commentStatus.ts';

// Helper function to safely parse dates
export const getValidDate = (dateString: string | null | undefined): Date => {
	if (!dateString) {
		return new Date(0); // Return earliest possible date if missing
	}

	// Ensure UTC timezone. Some createdAt values are missing the UTC timezone identifier, and some have it
	const normalizedDate = dateString.endsWith('Z') ? dateString : `${dateString}Z`;

	// Try to parse the date, return earliest date if invalid
	const parsed = new Date(normalizedDate);
	return isNaN(parsed.getTime()) ? new Date(0) : parsed;
};

interface FormatTimestampForAriaLabelProps {
	intl: any;
	dateString: string | null | undefined;
	commentStatus?: CommentStatus;
	resolveProperties?: CommentInlineResolveProperties | FooterResolvedProperties;
	isReply?: boolean;
}

/**
 * Formats a timestamp for use in aria labels and tooltips
 * @param props - Object containing intl, dateString, commentStatus, and isReply flag
 * @returns A formatted timestamp string suitable for aria labels and tooltips, or empty string if no date
 */
export const formatTimestampForAriaLabel = ({
	intl,
	dateString,
	commentStatus = CommentStatus.OPEN,
	resolveProperties,
	isReply = false,
}: FormatTimestampForAriaLabelProps): string => {
	if (!dateString) {
		return '';
	}

	// Get formatted timestamp
	let timestamp: string;
	try {
		const { formatTimeAgo } = require('@confluence/time-ago');
		timestamp = formatTimeAgo(intl, getValidDate(dateString));
	} catch (error) {
		// Fallback to a simple date format if formatTimeAgo is not available
		const date = getValidDate(dateString);
		timestamp = date.toLocaleDateString(intl.locale);
	}

	// For replies, just return the timestamp without comment status
	if (isReply) {
		return timestamp;
	}

	// Format based on comment status
	switch (commentStatus) {
		case CommentStatus.RESOLVED:
			return intl.formatMessage(commentsi18n.resolvedByInfoText, {
				friendlyDate: timestamp,
				username: resolveProperties?.resolvedUser,
			});
		case CommentStatus.REOPENED:
			return intl.formatMessage(commentsi18n.reopenedByTooltipTextCommentsPanel, {
				createdAtNonLocalized: timestamp,
				username: resolveProperties?.resolvedUser,
			});
		case CommentStatus.OPEN:
		default:
			return intl.formatMessage(commentsi18n.createdTimestamp, { timestamp });
	}
};
